import { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  FORMATO_SUMA_ASEGURADA_B,
  PORCENTAJE_DEV_B,
  SUMA_ASEGURADA_B,
  TIEMPO_AHORRO_B,
} from "../constants/quote";
import { usePlanService } from "@/services/plan.service";
import helpers from "@/utils/helpers";
import {
  setNameFrequency,
  typeOfFinancing,
  planSelected as planSelectedAction,
} from "@/redux/actions/plans";
import { usePlansContext } from "../context/PlansContext";
import { useQuote } from "./useQuote";
import { showModalLoading } from "@/redux/actions/global";
import { compareSumAssured } from "@/utils/Utils";

export const useLoadInitialData = ({
  paymentFrequencyList,
  setShowModalError,
}) => {
  const planSelected = useSelector((state) => state.plans.planSelected);
  const userData = useSelector((state) => state.vida.userData);
  const typeOfFinancingState = useSelector(
    (state) => state.plans.typeOfFinancing
  );
  const annualFeeNumber = typeOfFinancingState?.numeroCuotasAnual || null;

  const [clientAge] = useState(helpers.calcularEdad(userData.fechaNacimiento));
  const [sumAssured, setSumAssured] = useState("");
  const [savingTime, setSavingTime] = useState("");
  const [returnPercentage, setReturnPercentage] = useState("");
  const [frequency, setFrequency] = useState(
    annualFeeNumber ? String(annualFeeNumber) : "12"
  );
  const [arraySumAssured, setArraySumAssured] = useState([]);
  const [arraySavingTime, setArraySavingTime] = useState([]);
  const [arrayReturnPercentage, setArrayReturnPercentage] = useState([]);
  const [firstLoad, setFirstLoad] = useState(true);

  const {
    setLoadingSavingTime,
    setLoadingReturnPercentage,
  } = usePlansContext();

  const defaultSumAssuredFormat = FORMATO_SUMA_ASEGURADA_B;
  const defaultSumAssured = SUMA_ASEGURADA_B;
  const defaultTimeSelected = TIEMPO_AHORRO_B;
  const defaultDevPercentage = PORCENTAJE_DEV_B;

  const { getAttribute } = usePlanService();
  const { sendQuote } = useQuote({ setShowModalError });
  const dispatch = useDispatch();

  useEffect(() => {
    if (Object.keys(userData).length) initData();
  }, []);

  useEffect(() => {
    if (planSelected?.plan) {
      mappingFrequency(frequency);
    }
  }, [frequency, planSelected?.plan]);

  const initData = async () => {
    const {
      sumAssured: sumSelected,
      savingTime: savingTimeSelected,
      frequency: frequencySelected,
      returnPercentage: percentageSelected,
    } = planSelected;

    // Get data sum assured
    const filterSumAssured = await getSumAssured(sumSelected);

    // Get data saving time
    const listSavingTime = await getSavingTime(sumSelected);
    const filterSavingTime =
      listSavingTime.find(
        (e) => e.text === (savingTimeSelected?.text ?? `${defaultTimeSelected}`)
      ) || listSavingTime[0];
    setSavingTime(filterSavingTime);

    // Get data return percentage
    const listPercentage = await getReturnPercentage(filterSavingTime);
    const filterPercentageValue =
      listPercentage.find(
        (e) =>
          e.text === (percentageSelected?.text ?? `${defaultDevPercentage}%`)
      ) || listPercentage[0];
    setReturnPercentage(filterPercentageValue);

    dispatch(
      planSelectedAction({
        ...planSelected,
        sumAssured: filterSumAssured,
        savingTime: filterSavingTime,
        returnPercentage: filterPercentageValue,
      })
    );

    // Call service quote
    await sendQuote({
      sumAssured: filterSumAssured,
      savingTime: filterSavingTime,
      returnPercentage: filterPercentageValue,
      frequency: frequencySelected || frequency,
    });
    dispatch(showModalLoading(""));
    setFirstLoad(false);
  };

  const getSumAssured = async (sumSelected) => {
    try {
      const payload = {
        dscAtributo: "SUMA_ASEG_VCD",
      };
      const sumSelectedFormat = sumSelected?.text ?? defaultSumAssuredFormat;
      const response = await getAttribute(payload, "sumAssured");
      const filterValue = response.filter(
        (e) => e.text === sumSelectedFormat.replace(" ", "")
      )[0];
      setSumAssured(filterValue);
      setArraySumAssured(response);
      return filterValue;
    } catch (error) {
      setArraySumAssured([]);
    }
  };

  const mappingFrequency = async (frequency) => {
    const nameFrequency = paymentFrequencyList.find(
      (_) => _.value === frequency
    )?.name;
    await dispatch(setNameFrequency(nameFrequency));
    dispatch(
      typeOfFinancing(
        planSelected.plan.financiamiento.find(
          (element) => String(element.numeroCuotasAnual) === frequency
        )
      )
    );
  };

  const resetList = async (currentSumAssured, prevSumAssured) => {
    setLoadingSavingTime(true);
    setLoadingReturnPercentage(true);
    const result = compareSumAssured(currentSumAssured, prevSumAssured);
    const respSavingTime = await getSavingTime(currentSumAssured);
    const index = respSavingTime.findIndex(
      (item) => item.text === savingTime?.text
    );
    const arrayLength = respSavingTime.length;
    const newIndex = result ? 0 : arrayLength - 1;
    const newSavingTime = index < 0 ? respSavingTime[newIndex] : respSavingTime[index];

    if (respSavingTime.length > 0) {
      await getReturnPercentage(newSavingTime);
    }
  };

  const getSavingTime = async (currentSumAssured) => {
    setLoadingSavingTime(true);

    try {
      const payload = {
        dscAtributo: "PlanVidaDevVCD",
        valorAtributo:
          String(currentSumAssured?.text).replace(/[^\d]/g, "") ||
          defaultSumAssured,
      };
      const response = await getAttribute(payload, "savingTime", clientAge);
      setArraySavingTime(response);
      return response;
    } catch (error) {
      setArraySavingTime([]);
      return [];
    } finally {
      setLoadingSavingTime(false);
    }
  };

  const getReturnPercentage = async (currentSavingTime) => {
    setLoadingReturnPercentage(true);

    try {
      const payload = {
        dscAtributo: "PorcentDevolucionVCD",
        valorAtributo: `Plan ${currentSavingTime?.text}`,
        ideValor: currentSavingTime.value,
      };
      const list = await getAttribute(payload, "returnPercentage");
      const filterList =
        list.length > 6 ? list.filter((item) => item.text !== "120%") : list;
      setArrayReturnPercentage(filterList);
      return filterList;
    } catch (error) {
      setArrayReturnPercentage([]);
      return [];
    } finally {
      setLoadingReturnPercentage(false);
    }
  };

  return {
    arraySumAssured,
    arraySavingTime,
    arrayReturnPercentage,
    sumAssured,
    savingTime,
    returnPercentage,
    frequency,
    firstLoad,
    setSumAssured,
    setSavingTime,
    setReturnPercentage,
    setFrequency,
    resetList,
    getReturnPercentage,
  };
};
